<template>
	<app-content class="h-panel">
		<Tabs :datas="tabDatas" v-model="selected" @change="change"></Tabs>
			<div class="padding-top padding-right text-right">
				<Button @click="goInitImportPage" :loading="wizardLoading" color="primary">一键初始化导入</Button>
			<Button @click="downloadTemplate" :loading="downloadLoading" color="green">下载模板</Button>
			<Button :loading="importLoading" @click="doUpload">
				<input ref="file" type="file" accept=".xlsx,.xls,.csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv" style="display: none" @change="fileChange($event)">
				导入
			</Button>
			<Button @click="clearAllBalances" :loading="clearAllLoading" color="red">一键清空全部</Button>
			<Button @click="refreshParentBalance" :loading="refreshLoading" color="orange">刷新汇总</Button>
			<Button @click="trialBalance" :loading="trialBalanceLoading" color="blue">试算平衡</Button>
		</div>

		<!-- 期初余额汇总信息 - 只有点击试算平衡后才显示 -->
		<div class="padding balance-summary" v-if="balanceSummary && showBalanceSummary">
			<Row>
				<Cell :width="4">
					<div class="summary-card">
						<h4>期初余额汇总</h4>
						<div class="summary-item">
							<span class="label">借方合计：</span>
							<span class="amount debit">{{balanceSummary.totalDebit | numFormat}}</span>
						</div>
						<div class="summary-item">
							<span class="label">贷方合计：</span>
							<span class="amount credit">{{balanceSummary.totalCredit | numFormat}}</span>
						</div>
						<div class="summary-item">
							<span class="label">差额：</span>
							<span class="amount" :class="{'error': balanceSummary.difference > 0}">{{balanceSummary.difference | numFormat}}</span>
						</div>
						<div class="summary-item">
							<span class="label">平衡状态：</span>
							<span class="status" :class="{'balanced': balanceSummary.isInitialBalanced, 'unbalanced': !balanceSummary.isInitialBalanced}">
								{{balanceSummary.isInitialBalanced ? '平衡' : '不平衡'}}
							</span>
						</div>
					</div>
				</Cell>
				<Cell :width="4">
					<div class="summary-card">
						<h4>本年累计发生额汇总</h4>
						<div class="summary-item">
							<span class="label">借方累计：</span>
							<span class="amount debit">{{balanceSummary.totalCumulativeDebit | numFormat}}</span>
						</div>
						<div class="summary-item">
							<span class="label">贷方累计：</span>
							<span class="amount credit">{{balanceSummary.totalCumulativeCredit | numFormat}}</span>
						</div>
						<div class="summary-item">
							<span class="label">差额：</span>
							<span class="amount" :class="{'error': balanceSummary.cumulativeDifference > 0}">{{balanceSummary.cumulativeDifference | numFormat}}</span>
						</div>
						<div class="summary-item">
							<span class="label">平衡状态：</span>
							<span class="status" :class="{'balanced': balanceSummary.isCumulativeBalanced, 'unbalanced': !balanceSummary.isCumulativeBalanced}">
								{{balanceSummary.isCumulativeBalanced ? '平衡' : '不平衡'}}
							</span>
						</div>
					</div>
				</Cell>
				<Cell :width="4">
					<div class="summary-card">
						<h4>科目统计</h4>
						<div class="summary-item">
							<span class="label">科目总数：</span>
							<span class="count">{{balanceSummary.totalSubjects}}</span>
						</div>
						<div class="summary-item">
							<span class="label">末级科目：</span>
							<span class="count">{{balanceSummary.leafSubjects}}</span>
						</div>
						<div class="summary-item">
							<span class="label">有余额科目：</span>
							<span class="count">{{balanceSummary.subjectsWithBalance}}</span>
						</div>
					</div>
				</Cell>
			</Row>
		</div>

		<div class="h-panel-body padding" style="position: relative; min-height: 200px;">
			<Loading :loading="loading" text="正在获取科目数据..."></Loading>
			<table class="cus-table initial-balance-table">
				<thead class="header">
				<tr v-if="!isUnit">
					<td width="120">科目编码</td>
					<td nowrap>科目名称</td>
					<td width="80" nowrap>余额方向</td>
					<td width="150" nowrap>期初余额</td>
					<td width="150" nowrap>本年累计借方</td>
					<td width="150" nowrap>本年累计贷方</td>
					<td width="150" nowrap>年初余额</td>
					<td width="100" nowrap>操作</td>
				</tr>
				<template v-else>
					<tr>
						<td width="120" rowspan="2">科目编码</td>
						<td rowspan="2" nowrap>科目名称</td>
						<td width="80" rowspan="2" nowrap>余额方向</td>
						<td width="60" rowspan="2" nowrap>单位</td>
						<td colspan="5">期初录入</td>
						<td width="100" rowspan="2" nowrap>操作</td>
					</tr>
					<tr>
						<td width="120">数量</td>
						<td width="150">期初余额</td>
						<td width="150">本年累计借方</td>
						<td width="150">本年累计贷方</td>
						<td width="150">年初余额</td>
					</tr>
				</template>
				</thead>
				<tbody>
				<tr v-for="(item,i) in datas" :key="item.subjectId || item.code || i" :class="{'parent-row': !item.leaf, 'leaf-row': item.leaf, 'has-balance': item._hasBalance}">
					<td :style="{paddingLeft:(item.level!=1?(item.level-1)*20:10)+'px', textAlign: 'left'}" class="code-cell">
						<span class="subject-code">{{item.code}}</span>
						<span v-if="!item.leaf" class="parent-indicator" title="父级科目">📁</span>
						<span v-else class="leaf-indicator" title="末级科目">📄</span>
					</td>
					<td nowrap class="name-cell" style="text-align: left">
						<span class="subject-name">{{item.name}}</span>
						<i @click="addDetails(item)" class="fa fa-plus text-hover auxiliary-btn" v-if="item.auxiliaryAccounting" v-tooltip content="点击+号<br/>添加辅助明细"></i>
						<span v-if="item.auxiliaryAccounting" class="auxiliary-tag">辅助</span>
					</td>
					<td class="text-center direction-cell">
						<span class="balance-direction" :class="{'debit-direction': item.balanceDirection === '借', 'credit-direction': item.balanceDirection === '贷'}">
							{{item.balanceDirection}}
						</span>
					</td>

					<!-- 数量列（仅在有单位时显示） -->
					<td class="text-center" v-if="isUnit" :class="{'edit-hover':(!item._numEdit && item.leaf && !item.auxiliaryAccounting && item.unit), 'readonly': !item.leaf}">
						<template v-if="item.unit && item.leaf && !item.auxiliaryAccounting">
							<div class="editblock quantity-edit-block" v-if="!item._numEdit" @dblclick="showInput($event,item,'_numEdit',i)">{{item.num|numFormat}}</div>
							<NumberInput :id="'_numEdit'+i" v-width="120" v-if="item._numEdit" v-model="item.num" @change="balanceChange(item,'_numEdit')"/>
						</template>
						<template v-else-if="!item.leaf">
							<span class="parent-quantity" title="父级科目数量由子科目自动汇总">{{item.num|numFormat}}</span>
						</template>
						<template v-else>
							<span class="readonly-quantity">{{item.num|numFormat}}</span>
						</template>
					</td>

						<!-- 期初余额列 -->
						<td class="text-right balance-cell" :class="{'edit-hover':(!item._balanceEdit && item.leaf && !item.auxiliaryAccounting), 'readonly': !item.leaf}">
							<template v-if="item.leaf && !item.auxiliaryAccounting">
								<div class="editblock balance-edit-block" :class="{'debit-direction-bg': item.balanceDirection === '借', 'credit-direction-bg': item.balanceDirection === '贷'}"
								 :data-code="item.code" v-if="!item._balanceEdit" @dblclick="showInput($event,item,'_balanceEdit',i)">
									{{item._displayInitialBalance|numFormat}}
								</div>
							<NumberInput :id="'_balanceEdit'+i" v-width="150" v-if="item._balanceEdit" v-model="item.initialBalance" @change="initialBalanceChange(item,'_balanceEdit')"/>
						</template>
						<template v-else-if="!item.leaf">
							<span class="parent-balance" :class="{'debit-balance': item._displayInitialBalance > 0, 'credit-balance': item._displayInitialBalance < 0, 'zero-balance': item._displayInitialBalance === 0}"
								  title="父级科目期初余额由子科目自动汇总">{{item._displayInitialBalance|numFormat}}</span>
						</template>
						<template v-else>
							<span class="readonly-balance" :class="{'debit-balance': item._displayInitialBalance > 0, 'credit-balance': item._displayInitialBalance < 0, 'zero-balance': item._displayInitialBalance === 0}">
								{{item._displayInitialBalance|numFormat}}
							</span>
						</template>
					</td>

					<!-- 累计借方列 -->
					<td class="text-right" :class="{'edit-hover':(!item._cumDebitEdit && item.leaf && !item.auxiliaryAccounting), 'readonly': !item.leaf}">
						<template v-if="item.leaf && !item.auxiliaryAccounting">
							<div class="editblock" v-if="!item._cumDebitEdit" @dblclick="showInput($event,item,'_cumDebitEdit',i)">
								{{item.cumulativeDebit|numFormat}}
							</div>
							<NumberInput :id="'_cumDebitEdit'+i" v-width="150" v-if="item._cumDebitEdit" v-model="item.cumulativeDebit" @change="initialBalanceChange(item,'_cumDebitEdit')"/>
						</template>
						<template v-else>
							<span>{{item.cumulativeDebit|numFormat}}</span>
						</template>
					</td>

					<!-- 累计贷方列 -->
					<td class="text-right" :class="{'edit-hover':(!item._cumCreditEdit && item.leaf && !item.auxiliaryAccounting), 'readonly': !item.leaf}">
						<template v-if="item.leaf && !item.auxiliaryAccounting">
							<div class="editblock" v-if="!item._cumCreditEdit" @dblclick="showInput($event,item,'_cumCreditEdit',i)">
								{{item.cumulativeCredit|numFormat}}
							</div>
							<NumberInput :id="'_cumCreditEdit'+i" v-width="150" v-if="item._cumCreditEdit" v-model="item.cumulativeCredit" @change="initialBalanceChange(item,'_cumCreditEdit')"/>
						</template>
						<template v-else>
							<span>{{item.cumulativeCredit|numFormat}}</span>
						</template>
					</td>

					<!-- 年初余额列 -->
					<td class="text-right year-begin-cell readonly">
						<span class="readonly-balance"
							  :class="{'debit-balance': item._displayYearBeginBalance > 0, 'credit-balance': item._displayYearBeginBalance < 0, 'zero-balance': item._displayYearBeginBalance === 0}"
							  title="按期初余额和本年累计发生额反算的年初余额">
							{{item._displayYearBeginBalance|numFormat}}
						</span>
					</td>

					<!-- 操作列 -->
					<td class="text-center action-cell">
						<template v-if="item.leaf && !item.auxiliaryAccounting">
							<Button size="s" @click="clearBalance(item)" v-if="item._hasBalance" title="清空余额">清空</Button>
							<Button size="s" @click="copyFromTemplate(item)" color="blue" title="从模板复制">模板</Button>
						</template>
						<template v-else-if="!item.leaf">
							<span class="parent-action" title="父级科目不可操作">-</span>
						</template>
						<template v-else>
							<span class="auxiliary-action" title="辅助核算科目请点击+号添加明细">辅助</span>
						</template>
					</td>
				</tr>
				<tr v-if="!datas.length">
					<td :colspan="isUnit ? 9 : 8" class="text-center padding no-data">
						<span v-if="loading"><i class="h-icon-loading"></i> 数据正在努力加载中，请稍候...</span>
						<span v-else>暂无数据</span>
					</td>
				</tr>
				</tbody>
			</table>
		</div>
		<Modal v-model="showInitImportWizard" hasCloseIcon :maskClose="false">
			<div slot="header">一键初始化导入（科目余额表）</div>
			<div class="padding init-import-wizard">
				<div class="wizard-step">
					<div class="wizard-step-title">第一步：上传科目余额表（本币金额式）</div>
					<div class="wizard-step-content">
						<Button size="s" @click="triggerWizardBalanceUpload">选择余额表文件</Button>
						<input ref="wizardBalanceFile" type="file" accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" style="display: none" @change="wizardBalanceFileChange">
						<span class="wizard-file-name">{{wizardBalanceFileName || '未选择文件'}}</span>
						<div class="wizard-tip wizard-tip-block">当前账套编码：{{(currentAccountSets && currentAccountSets.encoding) || '-'}}</div>
						<div v-if="wizardDetectedEncoding" class="wizard-tip wizard-tip-block">
							自动识别层级：{{wizardDetectedEncoding}}
							<span v-if="wizardEncodingHasExtraLevels" class="red-color">（检测到超过系统支持上限{{maxSubjectLevel}}级，已按前{{maxSubjectLevel}}级推荐）</span>
						</div>
					</div>
				</div>

					<div class="wizard-step" v-if="wizardPreviewPlan">
						<div class="wizard-step-title">第二步：导入预演结果</div>
						<div class="wizard-step-content">
						<div>识别层级：{{wizardPreviewPlan.encodingDetected || '-'}}</div>
						<div>导入行数：{{wizardPreviewPlan.importRowTotal || 0}}</div>
						<div>期初导入条数：{{wizardPreviewPlan.initialBalanceTotal}}</div>
						<div>自动新增科目：{{(wizardPreviewPlan.mappingSummary && wizardPreviewPlan.mappingSummary.autoCreate) || 0}}</div>
						<div>需人工确认：{{(wizardPreviewPlan.mappingSummary && wizardPreviewPlan.mappingSummary.needConfirm) || 0}}</div>
						<div>试算借方：{{(wizardPreviewPlan.trialDebit || 0) | numFormat}}</div>
							<div>试算贷方：{{(wizardPreviewPlan.trialCredit || 0) | numFormat}}</div>
							<div>试算差额：{{(wizardPreviewPlan.trialDifference || 0) | numFormat}}</div>
							<div class="wizard-tip">预演已在后端执行并回滚，不会落库。</div>
							<div v-if="wizardPreviewPlan.trialBalanced === false" class="wizard-tip red-color">
								{{wizardPreviewPlan.blockingReason || '试算不平衡，请先调整科目映射后重新预演'}}
							</div>
						</div>
						<Table v-if="wizardPreviewMappings.length" :datas="wizardPreviewMappings" border class="wizard-mapping-table">
						<TableItem title="来源科目">
							<template slot-scope="{data}">
								{{data.sourceCode}}-{{data.sourceName}}
							</template>
						</TableItem>
						<TableItem title="系统科目">
							<template slot-scope="{data}">
								{{data.targetCode}}-{{data.targetName}}
							</template>
						</TableItem>
						<TableItem title="匹配策略" :width="120" align="center">
							<template slot-scope="{data}">
								{{formatMappingStrategy(data.strategy)}}
							</template>
						</TableItem>
						<TableItem title="方向" :width="70" align="center">
							<template slot-scope="{data}">
								{{data.balanceDirection || '-'}}
							</template>
						</TableItem>
						<TableItem title="期末余额" :width="140" align="right">
							<template slot-scope="{data}">
								{{data.endingBalance | numFormat}}
							</template>
						</TableItem>
						<TableItem title="确认" :width="90" align="center">
							<template slot-scope="{data}">
								<span :class="data.needConfirm ? 'red-color' : 'green-color'">{{data.needConfirm ? '需确认' : '自动'}}</span>
							</template>
						</TableItem>
							<TableItem title="调整目标科目" :width="180">
								<template slot-scope="{data}">
									<input
										v-if="data.sourceCode"
										type="text"
										:placeholder="data.targetCode || '输入系统科目编码'"
										v-model.trim="data.manualTargetCode"
										style="width: 160px;"
									/>
									<span v-else>-</span>
							</template>
						</TableItem>
					</Table>
					<div class="wizard-tip wizard-tip-block">
						可在“调整目标科目”中输入系统科目编码后，点击“导入预演”重新校验。留空表示沿用自动映射。
					</div>
				</div>
			</div>
			<div class="text-center">
				<Button color="blue" @click="runInitImportPreview" :loading="wizardLoading">导入预演</Button>
				<Button color="green" @click="startInitImport" :loading="wizardLoading">一键执行</Button>
				<Button @click="showInitImportWizard=false" :loading="wizardLoading">取消</Button>
			</div>
		</Modal>
		<Modal v-model="showModal" hasCloseIcon hasDivider>
			<div slot="header">期初试算结果</div>
			<Row class="result">
				<Cell :width="12" class="text-center" style="border-right: 1px solid #eeeeee">
					<div class="font-bold margin">期初余额</div>
					<div class="pic-body beginningBalance" :class="{left:beginningBalance.借<beginningBalance.贷,right:beginningBalance.借>beginningBalance.贷}">
						<span class="span1">{{beginningBalance.借|numFormat}}</span>
						<span class="span2">{{beginningBalance.借-beginningBalance.贷|numFormat}}</span>
						<span class="span3">{{beginningBalance.贷|numFormat}}</span>
						<div class="pic"></div>
					</div>
				</Cell>
				<Cell :width="12" class="text-center">
					<div class="font-bold margin">资产负债表期初</div>
					<div class="pic-body liabilities" :class="{left:liabilities.资产<liabilities.权益,right:liabilities.资产>liabilities.权益}">
						<span class="span1">{{liabilities.资产|numFormat}}</span>
						<span class="span2">{{liabilities.资产-liabilities.权益|numFormat}}</span>
						<span class="span3">{{liabilities.权益|numFormat}}</span>
						<div class="pic "></div>
					</div>
				</Cell>
			</Row>
		</Modal>
		<Modal v-model="showDetailModal" hasCloseIcon hasDivider>
			<div slot="header">增加明细</div>
			<Form v-width="400" :labelWidth="60" class="padding-right margin-top">
				<FormItem label="科目">
					<Select @change="subjectChange" v-model="auxiliary.subjectId" :datas="auxiliarys" :deletable="false" titleName="name" keyName="subjectId"></Select>
				</FormItem>
				<FormItem v-for="item in auxiliaryAccounting" :label="item.name" :key="item.id">
					<Select v-model="auxiliary.auxiliary[item.id]" :datas="auxiliaryAccountingData[auxiliary.subjectId]?auxiliaryAccountingData[auxiliary.subjectId][item.id]:[]" :deletable="false" titleName="name" keyName="id">
						<template slot-scope="{item}" slot="item">{{item.code}}-{{item.name}}</template>
					</Select>
				</FormItem>
			</Form>
			<div class="text-center margin-top">
				<Button color="green" @click="saveAuxiliary" :loading="loading">增加</Button>
				<Button @click="showDetailModal=false">取消</Button>
			</div>
		</Modal>
	</app-content>
</template>

<script>
	import {mapState} from 'vuex';
	import {MAX_SUBJECT_LEVEL} from '@/utils/subjectEncoding';
	import jQuery from 'jquery';

	const emptyForm = {
		"id": "0",
		"code": "",
		"name": "",
		"mnemonicCode": "",
		"balanceDirection": "",
		"status": "0",
		"parentId": "0"
	};

	export default {
		name: "Initial",
		data() {
			return {
				tabDatas: [
					{
						title: '资产',
						key: 'CodeAssets'
					}, {
						title: '负债',
						key: 'CodeLiabilities'
					}, {
						title: '权益',
						key: 'CodeInterest'
					}, {
						title: '成本',
						key: 'CodeCost'
					}, {
						title: '损益',
						key: 'CodeLoss'
					}],
				type: '资产',
				datas: [],
				form: Object.assign({}, emptyForm),
				showForm: false,
				showDetailModal: false,
				selected: 'CodeAssets',
				showModal: false,
				loading: false,
				downloadLoading: false,
				importLoading: false,
				clearAllLoading: false,
				refreshLoading: false, // 刷新汇总加载状态
				trialBalanceLoading: false, // 试算平衡加载状态
				temps: {},
				auxiliary: {subjectId: "", auxiliary: {}},
				auxiliaryAccounting: [],
				auxiliaryAccountingData: {},
				beginningBalance: {借: 0, 贷: 0},
				liabilities: {权益: 0, 资产: 0},
				balanceSummary: null, // 期初余额汇总信息
				showBalanceSummary: false, // 是否显示汇总信息
				showInitImportWizard: false,
				maxSubjectLevel: MAX_SUBJECT_LEVEL,
				wizardLoading: false,
				wizardBalanceFile: null,
				wizardBalanceFileName: '',
				wizardPreviewPlan: null,
					wizardPreviewMappings: [],
					wizardManualTargetMap: {},
					wizardDetectedEncoding: '',
					wizardEncodingHasExtraLevels: false,
					wizardBlockingReason: '',
				};
			},
		computed: {
			...mapState(['currentAccountSets']),
			isUnit() {
				return this.datas.some(value => value.unit);
			},
			auxiliarys() {
				return this.datas.filter(value => value.auxiliaryAccounting)
			}
		},
		watch: {
			type() {
				this.loadList();
			},
			'$route.query.initImport'(value) {
				if (value === '1') {
					this.goInitImportPage();
				}
			}
		},
		methods: {
			goInitImportPage() {
				this.$router.push({name: 'InitialImportCenter'});
			},
			consumeInitImportQuery() {
				if (!this.$route || !this.$route.query || this.$route.query.initImport !== '1') {
					return;
				}
				let query = Object.assign({}, this.$route.query);
				delete query.initImport;
				this.$router.replace({path: this.$route.path, query: query});
			},
			openInitImportWizard() {
				this.resetInitImportWizard();
				this.showInitImportWizard = true;
			},
			resetInitImportWizard() {
				this.wizardBalanceFile = null;
				this.wizardBalanceFileName = '';
				this.wizardPreviewPlan = null;
					this.wizardPreviewMappings = [];
					this.wizardManualTargetMap = {};
					this.wizardDetectedEncoding = '';
					this.wizardEncodingHasExtraLevels = false;
					this.wizardBlockingReason = '';
				},
			triggerWizardBalanceUpload() {
				this.$refs.wizardBalanceFile.click();
			},
			wizardBalanceFileChange() {
				if (!this.$refs.wizardBalanceFile.files.length) return;
				let file = this.$refs.wizardBalanceFile.files[0];
				this.$refs.wizardBalanceFile.value = "";
				this.wizardBalanceFile = file;
				this.wizardBalanceFileName = file.name;
					this.wizardPreviewPlan = null;
					this.wizardPreviewMappings = [];
					this.wizardManualTargetMap = {};
					this.wizardBlockingReason = '';
				},
			formatMappingStrategy(strategy) {
				let dict = {
					exact_code: '编码命中',
					exact_name: '名称命中',
					ai_recommend: 'AI推荐',
					fuzzy_name: '名称近似',
					auto_create: '自动新增',
					manual_override: '手工指定'
				};
				return dict[strategy] || strategy || '-';
			},
			async ensureWizardFilesReady() {
				if (!this.wizardBalanceFile) {
					throw new Error("请先选择科目余额表文件");
				}
			},
			buildWizardExecuteFormData() {
				let formData = new FormData();
				formData.append('balanceFile', this.wizardBalanceFile);
				let mappingAdjustments = this.collectWizardManualMappings();
				if (Object.keys(mappingAdjustments).length) {
					formData.append('mappingAdjustments', JSON.stringify(mappingAdjustments));
				}
				return formData;
			},
			collectWizardManualMappings() {
				let map = Object.assign({}, this.wizardManualTargetMap);
				(this.wizardPreviewMappings || []).forEach(item => {
					let sourceCode = item.sourceCode;
					if (!sourceCode) {
						return;
					}
					let manual = (item.manualTargetCode || '').trim();
					if (manual) {
						map[sourceCode] = manual;
					} else {
						delete map[sourceCode];
					}
				});
				this.wizardManualTargetMap = map;
				return map;
			},
			applyWizardMappings(rows = []) {
					this.wizardPreviewMappings = (rows || []).map(row => {
						let sourceCode = row.sourceCode;
						let manualTargetCode = sourceCode && this.wizardManualTargetMap[sourceCode] ? this.wizardManualTargetMap[sourceCode] : '';
						return Object.assign({}, row, {manualTargetCode});
					});
				},
				getAjaxErrorMessage(error, fallback = '未知错误') {
					if (!error) {
						return fallback;
					}
					return error.msg || error.message || fallback;
				},
				async runInitImportPreview() {
					try {
						this.wizardLoading = true;
						await this.ensureWizardFilesReady();
						let formData = this.buildWizardExecuteFormData();
						let {data} = await this.$api.setting.initialBalance.previewBalanceSheetInit(formData);
						this.wizardPreviewPlan = data || null;
						this.applyWizardMappings((data && data.mappingRows) ? data.mappingRows : []);
						this.wizardDetectedEncoding = (data && data.encodingDetected) || '';
						this.wizardEncodingHasExtraLevels = !!(data && data.encodingHasExtraLevels);
						this.wizardBlockingReason = (data && data.blockingReason) || '';
						if (data && data.trialBalanced === false) {
							this.$Message.warning("预演完成：试算不平衡，请先调整映射后重试");
						} else {
							this.$Message.success("导入预演通过（已回滚）");
						}
					} catch (error) {
						this.$Message.error("导入预演失败: " + this.getAjaxErrorMessage(error));
					} finally {
						this.wizardLoading = false;
					}
				},
				async startInitImport() {
					try {
						this.wizardLoading = true;
						await this.ensureWizardFilesReady();

						// 执行前强制再预演一次，确保采用用户最新调整映射并阻断不平衡导入。
						let previewFormData = this.buildWizardExecuteFormData();
						let {data} = await this.$api.setting.initialBalance.previewBalanceSheetInit(previewFormData);
						this.wizardPreviewPlan = data || null;
						this.applyWizardMappings((data && data.mappingRows) ? data.mappingRows : []);
						this.wizardDetectedEncoding = (data && data.encodingDetected) || '';
						this.wizardEncodingHasExtraLevels = !!(data && data.encodingHasExtraLevels);
						this.wizardBlockingReason = (data && data.blockingReason) || '';

						if (data && data.trialBalanced === false) {
							this.$Message.warning("试算不平衡，已阻止执行。请先调整映射后重新预演。");
							return;
						}

						let executeFormData = this.buildWizardExecuteFormData();
						await this.$api.setting.initialBalance.executeBalanceSheetInit(executeFormData);
					await this.$store.dispatch('init');
					this.$Message.success("一键初始化导入成功");
					this.showInitImportWizard = false;
						this.resetInitImportWizard();
						this.loadList();
					} catch (error) {
						this.$Message.error("一键初始化导入失败: " + this.getAjaxErrorMessage(error));
					} finally {
						this.wizardLoading = false;
					}
				},
			showInput(e, item, type, i) {
				this.$set(item, type, true);
				this.$nextTick(() => {
					jQuery('.h-input', '#' + type + i).focus().select()
				});
			},
			// 获取期初余额（直接使用后端计算好的数据）
			getInitialBalance(item) {
				// 优先使用后端计算好的期初余额，然后尝试其他字段
				return item.initialBalance || item.beginBalance || item.balance || 0;
			},
			getYearBeginBalance(item, initialBalance) {
				if (item.yearBeginBalance !== null && item.yearBeginBalance !== undefined) {
					return Number(item.yearBeginBalance) || 0;
				}
				if (item.type === '损益') {
					return 0;
				}
				const balance = initialBalance !== undefined ? Number(initialBalance) || 0 : this.getInitialBalance(item);
				const cumulativeDebit = Number(item.cumulativeDebit) || 0;
				const cumulativeCredit = Number(item.cumulativeCredit) || 0;
				if (item.balanceDirection === '贷') {
					return balance + cumulativeDebit - cumulativeCredit;
				}
				return balance - cumulativeDebit + cumulativeCredit;
			},
			// 根据期初余额和余额方向计算借方金额
			getDebitAmount(item) {
				// 优先使用API返回的debitAmount字段
				if (item.debitAmount !== null && item.debitAmount !== undefined) {
					return Number(item.debitAmount) || 0;
				}

				// 如果没有debitAmount，则根据余额方向计算
				const balance = this.getInitialBalance(item);
				if (item.balanceDirection === '借') {
					return balance > 0 ? balance : 0;
				} else {
					return balance < 0 ? Math.abs(balance) : 0;
				}
			},
			// 根据期初余额和余额方向计算贷方金额
			getCreditAmount(item) {
				// 优先使用API返回的creditAmount字段
				if (item.creditAmount !== null && item.creditAmount !== undefined) {
					return Number(item.creditAmount) || 0;
				}

				// 如果没有creditAmount，则根据余额方向计算
				const balance = this.getInitialBalance(item);
				if (item.balanceDirection === '贷') {
					return balance > 0 ? balance : 0;
				} else {
					return balance < 0 ? Math.abs(balance) : 0;
				}
			},
			// 检查是否有余额
			hasBalance(item) {
				return Math.abs(this.getInitialBalance(item)) > 0;
			},
			hasInitialData(item) {
				return Math.abs(Number(item.initialBalance) || 0) > 0
					|| Math.abs(Number(item.cumulativeDebit) || 0) > 0
					|| Math.abs(Number(item.cumulativeCredit) || 0) > 0
					|| Math.abs(Number(item.num) || 0) > 0;
			},
			// 清空余额
			clearBalance(item) {
				this.$Confirm(`确定要清空科目 ${item.code}-${item.name} 的全部期初数据吗？`, '确认清空', {type: 'warning'}).then(async () => {
						try {
							await this.$api.setting.initialBalance.clearSubject(item.subjectId);
							this.$Message.success('清空成功');
							this.loadList();
						} catch (error) {
							this.$Message.error('清空失败：' + (error.message || '未知错误'));
						}
				});
			},
			clearAllBalances() {
				this.$Confirm('确定要清空当前账套下全部期初数据吗？此操作会删除所有科目的期初余额、本年累计、数量以及辅助核算期初明细。', '确认一键清空', {type: 'warning'}).then(async () => {
						this.clearAllLoading = true;
						try {
							await this.$api.setting.initialBalance.clearAll();
							this.$Message.success('全部期初数据已清空');
							this.balanceSummary = null;
							this.showBalanceSummary = false;
							this.loadList();
						} catch (error) {
							this.$Message.error('清空失败：' + (error.message || '未知错误'));
						} finally {
							this.clearAllLoading = false;
						}
				});
			},
			// 从模板复制
			copyFromTemplate(item) {
				// TODO: 实现从模板复制功能
				this.$Message.info('模板复制功能开发中...');
			},
			// 期初余额变更
			initialBalanceChange(item, target) {
				// 确保期初余额为数字
				if (item.initialBalance === null || item.initialBalance === undefined || item.initialBalance === '') {
					item.initialBalance = 0;
				}
				// 确保本年累计借和贷为数字（清零时也要能保存）
				if (item.cumulativeDebit === null || item.cumulativeDebit === undefined || item.cumulativeDebit === '') {
					item.cumulativeDebit = 0;
				}
				if (item.cumulativeCredit === null || item.cumulativeCredit === undefined || item.cumulativeCredit === '') {
					item.cumulativeCredit = 0;
				}
				this.saveBalance(item);
				this.$set(item, target, false);
				// 移除自动汇总计算，只有点击试算平衡时才计算
			},
			// 保存余额
			saveBalance(item) {
				// 验证只有末级科目可以录入期初余额
				if (!item.leaf) {
					this.$Message.error('只有末级科目可以录入期初余额，科目 ' + item.code + '-' + item.name + ' 不是末级科目');
					return;
				}

				// 验证不是辅助核算科目
				if (item.auxiliaryAccounting) {
					this.$Message.error('辅助核算科目请点击+号添加明细，不能直接录入期初余额');
					return;
				}

				// 根据期初余额和余额方向计算借贷方金额
				const balance = item.initialBalance || 0;
				let debitAmount = 0;
				let creditAmount = 0;

				// 无论余额是否为0，都需要设置借贷方金额
				// 当余额为0时，借贷方都设为0，后端会正确处理清空操作
				if (item.balanceDirection === '借') {
					debitAmount = balance > 0 ? balance : 0;
					creditAmount = balance < 0 ? Math.abs(balance) : 0;
				} else {
					creditAmount = balance > 0 ? balance : 0;
					debitAmount = balance < 0 ? Math.abs(balance) : 0;
				}

				// 确保本年累计借和贷为数字（null/undefined/空字符串都转为0，确保清零能保存）
				const cumulativeDebit = (item.cumulativeDebit === null || item.cumulativeDebit === undefined || item.cumulativeDebit === '') ? 0 : Number(item.cumulativeDebit);
				const cumulativeCredit = (item.cumulativeCredit === null || item.cumulativeCredit === undefined || item.cumulativeCredit === '') ? 0 : Number(item.cumulativeCredit);

				let params = {
					summary: "期初",
					subjectName: `${item.code}-${item.name}`,
					subjectCode: item.code,
					subjectId: item.subjectId,
					accountSetsId: item.accountSetsId,
					debitAmount: debitAmount,
					creditAmount: creditAmount,
					cumulativeDebit: cumulativeDebit,
					cumulativeCredit: cumulativeCredit,
					num: item.num || null
				};

				this.$api.setting.initialBalance.save(params).then(() => {
					this.$Message.success('保存成功');
					// 重新加载数据以获取最新的父级科目汇总
					this.loadList();
				}).catch(error => {
					this.$Message.error('保存失败：' + (error.message || '未知错误'));
				});
			},
			// 更新余额汇总（财务软件标准：汇总所有科目类型的一级科目，参考科目余额表）
			updateBalanceSummary() {
				// 加载所有科目类型的数据进行汇总
				this.loadAllSubjectsForSummary();
			},

			// 注意：loadAllSubjectsForSummary 和 calculateBalanceSummary 方法已删除
			// 现在直接使用后端的试算平衡接口

			addDetails(item) {
				this.auxiliary.subjectId = item.subjectId;
				this.subjectChange(item);
				this.showDetailModal = true;
			},
			addTemp(data) {
				this.temps[data.id] = data;
				return data.id;
			},
			loadList() {
				this.loading = true;
				this.$api.setting.initialBalance.list(this.type).then(({data}) => {
					this.datas = data.map(item => {
						let initialBalance = 0;
						let debitAmount = 0;
						let creditAmount = 0;

						if (item.debitAmount !== null && item.debitAmount !== undefined) {
							debitAmount = item.debitAmount;
						}
						if (item.creditAmount !== null && item.creditAmount !== undefined) {
							creditAmount = item.creditAmount;
						}

						if (item.beginBalance !== null && item.beginBalance !== undefined) {
							initialBalance = item.beginBalance;
						} else {
							if (item.balanceDirection === '借') {
								initialBalance = debitAmount - creditAmount;
							} else {
								initialBalance = creditAmount - debitAmount;
							}
						}

						return {
							...item,
							debitAmount: debitAmount,
							creditAmount: creditAmount,
							initialBalance: initialBalance,
							beginBalance: initialBalance,
							yearBeginBalance: this.getYearBeginBalance(item, initialBalance),
							num: item.num || 0,
							_displayYearBeginBalance: this.getYearBeginBalance(item, initialBalance),
							_displayInitialBalance: initialBalance || 0,
							_hasBalance: this.hasInitialData({
								...item,
								initialBalance: initialBalance,
								num: item.num || 0
							})
						};
					});
					this.loading = false;
				}).catch(error => {
					this.loading = false;
					this.$Message.error('加载数据失败：' + (error.message || '未知错误'));
				});
			},
			calParent() {
				let cals = {'balance': 'beginBalance', 'debit': 'cumulativeDebit', 'credit': 'cumulativeCredit'};
				Object.keys(cals).forEach(key => {
					jQuery(`span.${key}`).each((i, item) => {
						let {code, index} = item.dataset;
						let total = 0;
						jQuery(`.${key}-edit-block[data-code^=${code}]`).each((i, numItem) => {
							if (numItem.dataset.num) {
								total += Number(numItem.dataset.num)
							}
						});
						this.datas[index][cals[key]] = total;
					});
				});

				this.datas = Array.from(this.datas);
			},
			change(data) {
				this.type = data.title;
			},
			balanceChange(balance, target) {
				// 这个方法主要用于数量编辑，保持原有逻辑
				// 根据期初余额和余额方向计算借贷方金额
				const initialBalance = balance.initialBalance || 0;
				let debitAmount = null;
				let creditAmount = null;

				if (initialBalance !== 0) {
					if (balance.balanceDirection === '借') {
						debitAmount = initialBalance > 0 ? initialBalance : 0;
						creditAmount = initialBalance < 0 ? Math.abs(initialBalance) : 0;
					} else {
						creditAmount = initialBalance > 0 ? initialBalance : 0;
						debitAmount = initialBalance < 0 ? Math.abs(initialBalance) : 0;
					}
				}

				// 确保本年累计借和贷为数字（null/undefined/空字符串都转为0，确保清零能保存）
				const cumulativeDebit = (balance.cumulativeDebit === null || balance.cumulativeDebit === undefined || balance.cumulativeDebit === '') ? 0 : Number(balance.cumulativeDebit);
				const cumulativeCredit = (balance.cumulativeCredit === null || balance.cumulativeCredit === undefined || balance.cumulativeCredit === '') ? 0 : Number(balance.cumulativeCredit);

				let params = {
					summary: "期初",
					subjectName: `${balance.code}-${balance.name}`,
					subjectCode: balance.code,
					subjectId: balance.subjectId,
					accountSetsId: balance.accountSetsId,
					debitAmount: debitAmount,
					creditAmount: creditAmount,
					cumulativeDebit: cumulativeDebit,
					cumulativeCredit: cumulativeCredit,
					num: balance.num || null
				};

				this.$api.setting.initialBalance.save(params).then(({data}) => {
					this.$set(balance, target, false);
					// 重新加载数据以获取后端计算的最新汇总
					this.loadList();
					this.$Message.success('保存成功');
				}).catch(error => {
					this.$Message.error('保存失败：' + (error.message || '未知错误'));
				});
			},
			// 刷新父级科目汇总
			async refreshParentBalance() {
				this.refreshLoading = true;
				try {
					console.log('开始刷新父级科目汇总');

					// 调用后端刷新父级科目汇总接口
					await this.$api.setting.initialBalance.refreshParentBalance();

					// 重新加载数据
					this.loadList();

					this.$Message.success('父级科目汇总刷新完成');

				} catch (error) {
					console.error('刷新父级科目汇总错误:', error);
					this.$Message.error('刷新失败：' + (error.message || '未知错误'));
				} finally {
					this.refreshLoading = false;
				}
			},
			// 试算平衡 - 直接调用后端接口获取汇总结果
			async trialBalance() {
				this.trialBalanceLoading = true;
				try {
					console.log('开始试算平衡');

					// 直接调用后端试算平衡接口
					const {data} = await this.$api.setting.initialBalance.trialBalance();
					console.log('后端返回的试算平衡结果:', data);

					// 设置汇总数据
					this.balanceSummary = data;
					this.showBalanceSummary = true;

					// 显示成功消息
					this.$Message.success('试算平衡计算完成');

				} catch (error) {
					console.error('试算平衡错误:', error);
					this.$Message.error('试算平衡失败：' + (error.message || '未知错误'));
				} finally {
					this.trialBalanceLoading = false;
				}
			},
			subjectChange(subject) {
				this.auxiliaryAccounting = JSON.parse(subject.auxiliaryAccounting);
				this.loadAuxiliaryAccountingData(subject);
			},
			loadAuxiliaryAccountingData(subject) {
				if (!this.auxiliaryAccountingData[subject.subjectId]) {
					this.$api.setting.accountingCategoryDetails.loadAuxiliaryAccountingData(this.auxiliaryAccounting).then(({data}) => {
						this.$set(this.auxiliaryAccountingData, subject.subjectId, data);
					});
				}
			},
			saveAuxiliary() {
				if (this.auxiliary.subjectId && Object.keys(this.auxiliary.auxiliary).length === this.auxiliaryAccounting.length) {
					this.loading = true;
					this.$api.setting.initialBalance.saveAuxiliary(this.auxiliary).then(({data}) => {
						this.loadList();
						this.showDetailModal = false;
					}).finally(() => {
						this.loading = false;
					});
				}
			},
			downloadTemplate() {
				this.downloadLoading = true;
				console.log('开始下载模板...');

				try {
					const downloadUrl = '/api/initial-balance/template';
					console.log('下载URL:', downloadUrl);

					// 使用window.open在新窗口中打开下载链接
					const downloadWindow = window.open(downloadUrl, '_blank');

					// 检查是否成功打开窗口
					if (downloadWindow) {
						console.log('下载窗口已打开');
						this.$Message("模板下载已开始");

						// 短暂延迟后关闭窗口（如果是下载，窗口会自动关闭）
						setTimeout(() => {
							try {
								downloadWindow.close();
							} catch (e) {
								// 忽略关闭窗口的错误
							}
						}, 2000);
					} else {
						throw new Error('无法打开下载窗口，可能被浏览器阻止');
					}

				} catch (error) {
					console.error('下载模板失败:', error);
					this.$Message.error("模板下载失败: " + error.message);
				} finally {
					console.log('模板下载完成，重置loading状态');
					this.downloadLoading = false;
				}
			},
			doUpload() {
				this.$refs.file.click();
			},
			fileChange(e) {
				if (this.$refs.file.files.length) {
					let formData = new FormData();
					formData.append('file', this.$refs.file.files[0]);
					this.importLoading = true;
					this.$api.setting.initialBalance.import(formData).then(({data}) => {
						this.$Message("期初余额导入成功~");
						this.loadList();
					}).catch((error) => {
						this.$Message.error("导入失败: " + (error.message || "未知错误"));
					}).finally(() => {
						this.importLoading = false;
					});
					this.$refs.file.value = "";
				}
			}
		},
		mounted() {
			if (this.currentAccountSets.accountingStandards !== 0) {
				this.tabDatas = [{
					title: '资产',
					key: 'CodeAssets'
				}, {
					title: '负债',
					key: 'CodeLiabilities'
				}, {
					title: '共同',
					key: 'Common'
				}, {
					title: '权益',
					key: 'CodeInterest'
				}, {
					title: '成本',
					key: 'CodeCost'
				}, {
					title: '损益',
					key: 'CodeLoss'
				}];
			}
			this.loadList();
			if (this.$route && this.$route.query && this.$route.query.initImport === '1') {
				this.goInitImportPage();
			}
		}
	};
</script>
<style lang="less" scoped>
	/* 期初余额表格样式优化 */
	.cus-table {
		/* 表格基本样式 */
	}

	.result {
		width: 500px;
		height: 250px;

		.pic {
			width: 238px;
			height: 120px;
			background-image: url("../../assets/settings.png");
			background-repeat: no-repeat;
			margin: 20px auto;
		}


		.pic-body {
			margin-top: 50px;
			position: relative;

			&.beginningBalance {
				.pic {
					background-position: -160px 0px;
				}

				span {
					position: absolute;
				}

				.span1 {
					top: 0;
					left: 22px;
				}

				.span2 {
					top: 50px;
					left: 0;
					width: 100%;
				}

				.span3 {
					top: 0;
					right: 10px;
				}

				&.right {
					.pic {
						height: 130px;
						background-position: -160px -132px;
					}

					.span1 {
						top: 25px;
					}

					.span3 {
						top: -25px;
					}
				}

				&.left {
					.pic {
						height: 130px;
						background-position: -160px -272px;
					}

					.span1 {
						top: -25px;
					}

					.span3 {
						top: 25px;
					}
				}
			}

			&.liabilities {
				.pic {
					background-position: -420px 5px;
				}

				span {
					position: absolute;
				}

				.span1 {
					top: 0;
					left: 22px;
				}

				.span2 {
					top: 50px;
					left: 0;
					width: 100%;
				}

				.span3 {
					top: 0;
					right: 10px;
				}

				&.right {
					.pic {
						height: 130px;
						background-position: -420px -132px;
					}

					.span1 {
						top: 25px;
					}

					.span3 {
						top: -25px;
					}
				}

				&.left {
					.pic {
						height: 130px;
						background-position: -420px -272px;
					}

					.span1 {
						top: -25px;
					}

					.span3 {
						top: 25px;
					}
				}
			}
		}
	}

	.editblock {
		width: 100%;
		height: 100%;
		line-height: 33px;
	}

	.init-import-wizard {
		.wizard-step {
			margin-bottom: 12px;
			padding: 10px 12px;
			border: 1px solid #ececec;
			border-radius: 4px;
			background-color: #fafafa;
		}

		.wizard-step-title {
			font-weight: 600;
			margin-bottom: 8px;
		}

		.wizard-step-content {
			line-height: 32px;
		}

		.wizard-tip {
			margin-left: 10px;
			color: #8c8c8c;
		}

		.wizard-tip-block {
			margin-left: 0;
			line-height: 24px;
		}

		.wizard-file-name {
			margin-left: 10px;
			color: #606266;
		}

		.wizard-mapping-table {
			margin-top: 8px;
		}
	}

	/* 期初余额页面样式 */
	.initial-balance-table {
		.parent-row {
			background-color: #f8f9fa;
			font-weight: 500;
		}

		.leaf-row {
			&.has-balance {
				background-color: #fff3cd;
			}
		}

		.code-cell {
			.subject-code {
				font-family: 'Courier New', monospace;
				font-weight: 500;
			}

			.parent-indicator, .leaf-indicator {
				margin-left: 5px;
				font-size: 12px;
			}
		}

		.name-cell {
			.subject-name {
				font-weight: 500;
			}

			.auxiliary-btn {
				margin-left: 8px;
				color: #007bff;
				cursor: pointer;

				&:hover {
					color: #0056b3;
				}
			}

			.auxiliary-tag {
				background-color: #17a2b8;
				color: white;
				font-size: 10px;
				padding: 2px 4px;
				border-radius: 2px;
				margin-left: 5px;
			}
		}

		.direction-cell {
			.balance-direction {
				padding: 2px 8px;
				border-radius: 3px;
				font-weight: 500;

				&.debit-direction {
					background-color: #d4edda;
					color: #155724;
				}

				&.credit-direction {
					background-color: #f8d7da;
					color: #721c24;
				}
			}
		}

		.balance-cell {
			.editblock {
				min-height: 24px;
				padding: 4px 8px;
				border: 1px solid transparent;
				border-radius: 3px;

				&:hover {
					border-color: #007bff;
					background-color: #f8f9fa;
				}
			}

			.balance-edit-block {
				&.debit-direction-bg {
					background-color: #d4edda;
					border-left: 3px solid #28a745;
				}

				&.credit-direction-bg {
					background-color: #f8d7da;
					border-left: 3px solid #dc3545;
				}
			}

			.parent-balance {
				color: #6c757d;
				font-style: italic;
				cursor: help;
			}

			.readonly-balance {
				color: #6c757d;
			}
		}

		/* 期初余额显示样式 */
		.initial-balance, .parent-balance, .readonly-balance {
			font-weight: 600;
			padding: 4px 8px;
			border-radius: 3px;

			&.debit-balance {
				background-color: #d4edda;
				color: #155724;
			}

			&.credit-balance {
				background-color: #f8d7da;
				color: #721c24;
			}

			&.zero-balance {
				color: #6c757d;
			}
		}

		.action-cell {
			.parent-action, .auxiliary-action {
				color: #6c757d;
				font-style: italic;
			}
		}

		.readonly {
			background-color: #f8f9fa;
			color: #6c757d;
		}
	}

	/* 余额汇总卡片样式 */
	.balance-summary {
		background-color: #f8f9fa;
		border-radius: 6px;
		margin-bottom: 15px;

		.summary-card {
			background: white;
			border-radius: 6px;
			padding: 15px;
			box-shadow: 0 2px 4px rgba(0,0,0,0.1);

			h4 {
				margin: 0 0 15px 0;
				color: #333;
				font-size: 16px;
				border-bottom: 2px solid #007bff;
				padding-bottom: 5px;
			}

			.summary-item {
				display: flex;
				justify-content: space-between;
				align-items: center;
				margin-bottom: 8px;

				.label {
					font-weight: 500;
					color: #555;
				}

				.amount {
					font-family: 'Courier New', monospace;
					font-weight: 600;

					&.debit {
						color: #155724;
					}

					&.credit {
						color: #721c24;
					}

					&.error {
						color: #dc3545;
					}
				}

				.count {
					font-weight: 600;
					color: #007bff;
				}

				.status {
					font-weight: 600;
					padding: 2px 8px;
					border-radius: 3px;

					&.balanced {
						background-color: #d4edda;
						color: #155724;
					}

					&.unbalanced {
						background-color: #f8d7da;
						color: #721c24;
					}
				}
			}
		}
	}

	.edit-hover:hover {
		cursor: pointer;
		background-color: @gray2-color;
	}
</style>
